import React from 'react'
import AgTechTourHelpButton from '@agtech/web/components/App/Tour/AgTechTourHelpButton';
import { TaskTemplateItemDialogTourConfig } from './TaskTemplateItemDialogTourConfig';
import { AgTechDataAction } from '@agtech/core/data/actions/AgTechDataActions';
import { AgTechHttpResponses } from '@agtech/core/data/actions/AgTechHttpRequests';
import { queryResult } from '@agtech/core/data/queries/AgTechDataQueries';
import { AgTechButtonProps, AgTechButton, AgTechClickEventHandler } from '@agtech/web/components/Buttons/AgTechButtons';
import { AgTechFormNumberControl } from '@agtech/web/components/Forms/Controls/AgTechFormNumberControl';
import { AgTechFormTextAreaControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextAreaControl';
import { AgTechFormTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextControl';
import AgTechFormBody from '@agtech/web/components/Forms/Layout/AgTechFormBody';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import AgTechFormDialog from '@agtech/web/components/Portals/Dialogs/AgTechFormDialog';
import { DefaultSaleTaskTemplateItem, SaleTaskTemplateItem } from '@sales/data/taskTemplates/TaskTemplateData';

export type TaskTemplateItemDialogProps = {
    item?: SaleTaskTemplateItem,
    onItemSaved: (item: SaleTaskTemplateItem) => void
}

// This dialog never talks to the server - it only edits a SaleTaskTemplateItem in memory and hands
// it back to the parent TaskTemplateDialog's local items list. Everything is persisted together in
// one call when the template itself is submitted (see useTaskTemplateWriteWithItemsAction).
const localSaveAction: AgTechDataAction<SaleTaskTemplateItem, SaleTaskTemplateItem> = {
    name: 'LocalSaveTaskTemplateItem',
    getConfiguration: () => ({
        actionExecutionMessage: 'Saving...',
        actionConfirmationMessage: 'Item saved',
        showConfirmationMessage: false
    }),
    action: async props => AgTechHttpResponses.Success(props.submittedEntity)
}

const TaskTemplateItemDialog = (props: TaskTemplateItemDialogProps) => {
    return (
        <AgTechFormDialog
            width={550}
            structure={() => ({
                header: {
                    header: props.item ? 'Update Item' : 'Add Item',
                    settings: {
                        content: {
                            body: () => <AgTechTourHelpButton tourConfig={TaskTemplateItemDialogTourConfig} />
                        }
                    }
                },
                submitButtonText: props.item ? 'Update Item' : 'Add Item',
            })}
            dialogData={{
                query: queryResult(props.item ?? DefaultSaleTaskTemplateItem(0)),
                getDialogData: item => item
            }}
            dialogAction={{
                action: localSaveAction,
                getActionData: d => d
            }}
            dialogBehavior={() => ({
                eventHandlers: {
                    onDialogSubmitted: async savedItem => {
                        props.onItemSaved(savedItem);
                    }
                }
            })}
        >
            {(formContext) => (
                <AgTechFormBody>
                    <div id='task-item-name'>
                    <AgTechFormRow>
                        <AgTechFormTextControl
                            entityType={formContext.entity}
                            caption='Task Name'
                            value={item => item.taskName}
                            focused={true}
                            required={true}
                            width={100}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                taskName: val
                            })}
                        />
                    </AgTechFormRow>
                    </div>
                    <div id='task-item-description'>
                    <AgTechFormRow>
                        <AgTechFormTextAreaControl
                            entityType={formContext.entity}
                            caption='Description'
                            value={item => item.taskDescription}
                            rows={3}
                            width={100}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                taskDescription: val
                            })}
                        />
                    </AgTechFormRow>
                    </div>
                    <div id='task-item-priority'>
                    <AgTechFormRow>
                        <AgTechFormNumberControl
                            entityType={formContext.entity}
                            caption='Priority'
                            value={item => item.priority}
                            width={33}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                priority: val
                            })}
                        />
                        <AgTechFormNumberControl
                            entityType={formContext.entity}
                            caption='Days Between Notifications'
                            value={item => item.daysBetweenNotifications}
                            width={33}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                daysBetweenNotifications: val
                            })}
                        />
                    </AgTechFormRow>
                    </div>
                    <div id='task-item-offsets'>
                    <AgTechFormRow>
                        <AgTechFormNumberControl
                            entityType={formContext.entity}
                            caption='Start Offset (days from sale start)'
                            value={item => item.startOffsetDays}
                            width={50}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                startOffsetDays: val
                            })}
                        />
                        <AgTechFormNumberControl
                            entityType={formContext.entity}
                            caption='End Offset (days from sale start)'
                            value={item => item.endOffsetDays}
                            width={50}
                            onControlValueChanged={async (e, val) => ({
                                ...e,
                                endOffsetDays: val
                            })}
                        />
                    </AgTechFormRow>
                    </div>
                </AgTechFormBody>
            )}
        </AgTechFormDialog>
    )
}

TaskTemplateItemDialog.Button = (props: TaskTemplateItemDialogProps & AgTechButtonProps) => (
    <AgTechButton
        text={props.item ? 'Edit' : 'Add Item'}
        icon={props.item ? 'fas fa-edit' : 'fas fa-plus'}
        {...props}
        action={e => TaskTemplateItemDialog.Open(e, props)}
    />
);

TaskTemplateItemDialog.Open = (e: AgTechClickEventHandler, props: TaskTemplateItemDialogProps) => e.dialogs.openDialog({
    dialog: () => <TaskTemplateItemDialog item={props.item} onItemSaved={props.onItemSaved} />
});

export default TaskTemplateItemDialog;
